<script setup>
import { withTrailingSlash } from '@/utils/siteUrl'

defineProps({
  slug: { type: String, required: true },
  title: { type: String, required: true },
  date: { type: String, required: true },
  excerpt: { type: String, required: true },
  author: { type: String, default: '' }
})

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric'
  })
}
</script>

<template>
  <RouterLink :to="withTrailingSlash(`/about/news/${slug}`)" class="news-card" :aria-label="`${title} — ${formatDate(date)}`">
    <div class="meta">
      <time :datetime="date" class="date">{{ formatDate(date) }}</time>
      <span v-if="author" class="author">{{ author }}</span>
    </div>
    <h3 class="title">{{ title }}</h3>
    <p class="excerpt">{{ excerpt }}</p>
    <span class="cta" aria-hidden="true">Read more →</span>
  </RouterLink>
</template>

<style lang="scss" scoped>
.news-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: $space-3;
  padding: $space-6;
  background: var(--color-surface-warm);
  border: 1px solid var(--color-border);
  border-radius: $radius;
  text-decoration: none;
  color: inherit;
  @include transition(box-shadow, background, transform);

  &:hover {
    background: var(--color-surface);
    box-shadow: 0 4px 6px rgba($color-ink, 0.16);
    transform: translateY(-2px);
    .cta { background-size: 100% 1px; }
  }
  &:focus-visible {
    @include focus-ring;
    .cta { background-size: 100% 1px; }
  }

  .meta {
    display: flex;
    gap: $space-3;
    align-items: center;
  }

  .date {
    font-size: $font-size-cap;
    color: var(--color-text-muted);
  }

  .author {
    font-size: $font-size-cap;
    color: var(--color-text-muted);
    &::before { content: '·'; margin-right: $space-3; }
  }

  .title {
    font-size: $font-size-h3;
    font-weight: 600;
    color: var(--color-ink);
    line-height: $lh-heading;
  }

  .excerpt {
    font-size: $font-size-base;
    color: var(--color-text);
    line-height: $lh-base;
    flex: 1;
  }
}
</style>
